import { computed } from 'vue'
import { useTranslate } from "@zotlo-shared/composable/useTranslate";
import { usePriceInfo } from '@zotlo-shared/composable/usePriceInfo';
import { getDocumentLinks } from '@zotlo-shared/utils/getDocumentLinks';
import { DOCS } from '@zotlo-shared/types';
import { getPackageTypeConditions } from '@zotlo-shared/elements/common/payment/utils';

export function useApplyLinks(props: Record<string, any>) {
  const { translate, $te } = useTranslate();

  const RUSSIA_DOCUMENTS = {
    payment_aggregator: 'https://3p-assets.cdnztl.com/docs/2025/09/10/jigle-payment-terms-ru.pdf',
  }

  const packageTypeData = computed(() => getPackageTypeConditions(props.pageData?.selectedPackage, { isTrialUsed: props.pageData?.isTrialUsed }));

  const linksByAttr = computed(() => {
    const countryCode = props.pageData?.countryCode;
    const isWebDeeplinkActive = !!props.pageData?.deeplinkSettings?.useDeeplink?.generic;

    const docLinks = getDocumentLinks({
      countryCode: countryCode?.toLowerCase() ?? 'en',
      lang: props.locale,
      links: props.pageData?.docLinks || {}
    });

    return {
      "data-privacy-policy-link": props.pageData?.applicationLinks?.privacyUrl,
      "data-terms-of-use-link": props.pageData?.applicationLinks?.termsOfServiceUrl,
      "data-customer-service-link": props.pageData?.applicationLinks?.customerSupportUrl,
      "data-generic-app-link": props.pageData?.applicationLinks?.[isWebDeeplinkActive ? 'deeplinkWeb' : 'genericDownloadUrl'],
      "data-zotlo-privacy-policy-link": docLinks?.[DOCS.PRIVACY_POLICY],
      "data-zotlo-terms-of-use-link": docLinks?.[DOCS.TERMS_OF_SERVICE],
      "data-payment-aggregator": RUSSIA_DOCUMENTS.payment_aggregator,
    }
  });

  const linkParams = computed(() => {
    return {
      zotloTermsLink: `<a data-zotlo-terms-of-use-link>${translate('generate.common.terms', props.locale)}</a>`,
      zotloTermsOfUseLink: `<a data-zotlo-terms-of-use-link>${translate('generate.common.termsOfUse', props.locale)}</a>`,
      zotloPrivacyPolicyLink: `<a data-zotlo-privacy-policy-link>${translate('generate.common.privacyPolicy', props.locale)}</a>`,
      privacyPolicyLink: `<a data-privacy-policy-link>${translate('generate.common.privacyPolicy', props.locale)}</a>`,
      termsLink: `<a data-terms-of-use-link>${translate('generate.common.terms', props.locale)}</a>`,
      termsOfUseLink: `<a data-terms-of-use-link>${translate('generate.common.termsOfUse', props.locale)}</a>`,
      paymentAggregatorLink: `<a data-payment-aggregator>${translate('generate.common.here', props.locale)}</a>`,
    }
  })

  const priceInfoValue = usePriceInfo(props);

  const params = computed(() => {
    const isRussia = props.pageData?.countryCode === 'RU';
    const isActiveFTC = !!props.pageData?.isActiveFTC;
    const statementName = props.isEditorMode ? '{statementName}' : props.pageData?.statementName || '';
    const appName = props.isEditorMode ? '{appName}' : props.pageData?.appName || '';
    const jigle = translate('generate.legalDocInfoElement.jigleName');
    const zotloName = isRussia ? jigle : translate('generate.legalDocInfoElement.zotloName');
    const zotloTitle = isRussia ? jigle : translate('generate.legalDocInfoElement.zotloTitle');

    const localizationParams = {
      appName,
      statementName,
      zotloName,
      zotloTitle,
      ...linkParams.value
    }

    const priceInfo = isActiveFTC && !packageTypeData.value?.isOneTimePayment ? '' : `<p>${priceInfoValue.value}</p><br/>`;
    const aggreementInfo = `<p>${translate('generate.legalDocInfoElement.agreementInfo', props.locale, localizationParams)}</p><br/>`
    const aggrementMoreInfo = `<p>${translate('generate.legalDocInfoElement.agreementMoreInfo', props.locale, localizationParams)}</p><br/>`;
    const chargeStatement = isRussia ? '' : `<p>${translate('generate.legalDocInfoElement.chargeStatement', props.locale, localizationParams)}</p><br/>`;
    const aggregatorLine = isRussia ? `<p>${translate('generate.legalDocInfoElement.expandedRussiaAdditional', props.locale, localizationParams)}</p><br/>` : '';
    const addressLine = isRussia ? '' : `<p>${translate('generate.legalDocInfoElement.zotloAddress', props.locale, localizationParams)}</p>`;

    return {
      priceInfo,
      aggreementInfo,
      aggrementMoreInfo,
      chargeStatement,
      aggregatorLine,
      addressLine,
      ...props.element.options?.params
    }
  });

  const textValue = computed(() => {
    const isThereAnyParam = props.value.includes('{') && props.value.includes('}');
    const isValidKey = $te(props.value);
    const translated = isValidKey || isThereAnyParam ? translate(props.value, props.locale, params.value) : props.value;

    return props.isEditorMode
      ? translated
      : Object.entries(linksByAttr.value).reduce((acc, [key, value]) => {
          return acc.replace(new RegExp(key, 'g'), `${key} href="${value || '#'}" target="_blank"`)
        }, translated)
  });

  const linksAttrs = computed(() => {
    if (props.isEditorMode || props.element.tagName !== 'a') return {};

    return Object.entries(props.element.attr)
      .filter(([key]) => /^data-/.test(key))
      .reduce((acc, item) => {
        const key = item[0] as keyof typeof linksByAttr.value;
        const val = linksByAttr.value[key];
        if (val) {
          acc['href'] = val || '#';
          acc['target'] = '_blank'
        }
        return acc;
      }, {} as Record<string, any>)
  })

  return {
    textValue,
    linksAttrs,
  }
}
